import React, { useState, useEffect, useCallback } from "react";
import axios from '../../../../api/apiClient';
import { domain, token } from '../../../Security';

// Icons
import CloseIcon from "@mui/icons-material/Close";
import RateReviewIcon from '@mui/icons-material/RateReview';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

// Common Components
import { Input, TextArea, DateInput } from '../../../../common/Input';
import { Select } from "../../../../common/Select";
import { LoadingSpinner } from "../../../../common/Loading";
import { ConfirmationModal } from '../../../../common/Modal';
import { EmployeePickerField } from "../../../../common/selectComponents/EmployeePicker";

export const EmployeeRequestModal = ({ isOpen, onClose, mode, selectedData, showGeneralMessage, user }) => {
	const [loading, setLoading] = useState(false);
	const [dropdownsLoading, setDropdownsLoading] = useState(false);

	// Confirmation Modal States
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [confirmationConfig, setConfirmationConfig] = useState({
		title: "",
		message: "",
		type: "info",
		action: null
	});

	// Select Modal States

	const [form, setForm] = useState({
		requesterId: user?.employeePersonalInformationId || "",
		employeePersonalInformationId: "",
		employeeData: null,
		approverId: "",
		approverData: null,
		authorizeSupervisorId: "",
		supervisorData: null,
		requestNumber: "",
		vesselId: "",
		title: "",
		remarks: "",
		requestDate: new Date().toISOString().split('T')[0],
		requestCode: "",
		effectiveDate: "",
		status: "pending",
		approverRemarks: "",
		authorizedSignatureVP: "",
	});

	const [options, setOptions] = useState({ vessels: [] });

	const fetchStaticDropdowns = useCallback(async () => {
		try {
			setDropdownsLoading(true);
			const [vesRes] = await Promise.all([
				axios.get(`${domain}/api/Vessel`, { headers: { 'Authorization': `Bearer ${token}` } })
			]);
			setOptions({ vessels: vesRes.data.map(v => ({ value: v.id, label: v.name })) });
		} catch (error) {
			console.error("Fetch dropdowns error", error);
		} finally {
			setDropdownsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			fetchStaticDropdowns();
			if (selectedData) {
				setForm({
					...selectedData,
					employeePersonalInformationId: selectedData.employeePersonalInformationId || "",
					approverId: selectedData.approverEmployeePersonalInformationId || "",
					authorizeSupervisorId: selectedData.supervisorEmployeePersonalInformationId || "",
					vesselId: selectedData.vesselId || "",
					employeeData: selectedData.employeePersonalInformationId ? {
						fullName: selectedData.employeePersonalInformationFullName,
						employeeId: selectedData.employeePersonalInformationEmployeeId,
						position: selectedData.employeePersonalInformationPosition,
						department: selectedData.employeePersonalInformationDepartment
					} : null,
					approverData: selectedData.approverEmployeePersonalInformationId ? {
						fullName: selectedData.approverEmployeePersonalInformationFullName,
						position: ""
					} : null,
					supervisorData: selectedData.supervisorEmployeePersonalInformationId ? {
						fullName: selectedData.supervisorEmployeePersonalInformationFullName,
						position: ""
					} : null,
					requestDate: selectedData.requestDate?.split('T')[0] || "",
					effectiveDate: selectedData.effectiveDate?.split('T')[0] || "",
					authorizedSignatureVP: selectedData.authorizedSignatureVP || (mode === "approve" ? user?.employeePersonalInformationFullName : ""),
				});
			} else {
				resetForm();
			}
		}
	}, [isOpen, selectedData, fetchStaticDropdowns, mode, user]);

	const resetForm = () => {
		setForm({
			requesterId: user?.employeePersonalInformationId || "",
			employeePersonalInformationId: "",
			employeeData: null,
			approverId: "",
			approverData: null,
			authorizeSupervisorId: "",
			supervisorData: null,
			requestNumber: "",
			vesselId: "",
			title: "",
			remarks: "",
			requestDate: new Date().toISOString().split('T')[0],
			requestCode: "",
			effectiveDate: "",
			status: "pending",
			approverRemarks: "",
			authorizedSignatureVP: ""
		});
	};

	const handleInputChange = (field, value) => {
		setForm(prev => ({ ...prev, [field]: value }));
	};

	// --- TRIGGER CONFIRMATION HELPER ---
	const triggerConfirm = (title, message, type, action) => {
		setConfirmationConfig({ title, message, type, action });
		setShowConfirmation(true);
	};

	// --- SAVE / UPDATE LOGIC ---
	const handleSave = () => {
		if (!form.employeePersonalInformationId || !form.approverId || !form.vesselId) {
			showGeneralMessage("Please select an Employee, Approver, and Vessel.", false);
			return;
		}

		const isEdit = mode === "edit";
		triggerConfirm(
			isEdit ? "Update Request" : "Submit Request",
			`Are you sure you want to ${isEdit ? "update" : "submit"} this employee request?`,
			"info",
			executeSave
		);
	};

	const executeSave = async () => {
		try {
			setLoading(true);
			const url = `${domain}/api/EmployeeRequest`;
			const payload = {
				id: selectedData?.id || 0,
				requestNumber: form.requestNumber,
				requesterEmployeePersonalInformationId: parseInt(user?.employeePersonalInformationId),
				employeePersonalInformationId: parseInt(form.employeePersonalInformationId),
				approverEmployeePersonalInformationId: parseInt(form.approverId),
				supervisorEmployeePersonalInformationId: parseInt(form.authorizeSupervisorId),
				vesselId: parseInt(form.vesselId),
				requestCode: form.requestCode,
				title: form.title,
				effectiveDate: form.effectiveDate,
				requestDate: form.requestDate,
				remarks: form.remarks,
				status: form.status,
				isActive: true,
				updatedById: parseInt(user?.id)
			};

			if (mode === "add") {
				payload.createdById = parseInt(user?.id);
				await axios.post(url, payload, { headers: { 'Authorization': `Bearer ${token}` } });
			} else {
				await axios.put(`${url}/${selectedData.id}`, payload, { headers: { 'Authorization': `Bearer ${token}` } });
			}
			showGeneralMessage("Success!", true);
			onClose();
		} catch (e) {
			showGeneralMessage("Error processing request.", false);
		} finally {
			setLoading(false);
			setShowConfirmation(false);
		}
	};

	// --- APPROVE / REJECT LOGIC ---
	const handleApproveAction = (isApproved) => {
		if (!form.approverRemarks) {
			showGeneralMessage("Approver remarks are required to take an action.", false);
			return;
		}

		triggerConfirm(
			isApproved ? "Approve Request" : "Reject Request",
			`Are you sure you want to ${isApproved ? "APPROVE" : "REJECT"} this request?`,
			isApproved ? "success" : "danger",
			() => executeApproveAction(isApproved)
		);
	};

	const executeApproveAction = async (isApproved) => {
		try {
			setLoading(true);
			const newStatus = isApproved ? "approved" : "rejected";
			const payload = {
				status: newStatus,
				approverRemarks: form.approverRemarks,
				authorizedSignatureVP: form.authorizedSignatureVP,
				updatedById: parseInt(user?.id)
			};
			await axios.put(`${domain}/api/EmployeeRequest/${selectedData.id}/approve-action`, payload, {
				headers: { 'Authorization': `Bearer ${token}` }
			});
			showGeneralMessage(`Request has been ${newStatus} successfully.`, true);
			onClose();
		} catch (e) {
			showGeneralMessage("Failed to process approval.", false);
		} finally {
			setLoading(false);
			setShowConfirmation(false);
		}
	};

	// --- CANCEL LOGIC ---
	const handleCancel = () => {
		triggerConfirm(
			"Cancel Request",
			"Are you sure you want to cancel this request? This action cannot be undone.",
			"danger",
			executeCancel
		);
	};

	const executeCancel = async () => {
		try {
			setLoading(true);
			await axios.put(`${domain}/api/EmployeeRequest/${selectedData.id}/cancel`,
				{ updatedById: parseInt(user?.id) },
				{ headers: { 'Authorization': `Bearer ${token}` } }
			);
			showGeneralMessage("Request has been cancelled.", true);
			onClose();
		} catch (e) {
			showGeneralMessage("Failed to cancel request.", false);
		} finally {
			setLoading(false);
			setShowConfirmation(false);
		}
	};

	// Employee Select Handlers
	const handleSelectTarget = (emp) => {
		handleInputChange('employeePersonalInformationId', emp.id);
		handleInputChange('employeeData', {
			fullName: emp.fullName,
			employeeId: emp.employeeId,
			position: emp.employeeInformationPositionDescription || "N/A",
			department: emp.employeeInformationDepartmentDescription || "N/A"
		});
		handleInputChange('title', emp.employeeInformationPositionDescription);
	};

	const handleSelectApprover = (emp) => {
		handleInputChange('approverId', emp.id);
		handleInputChange('approverData', { fullName: emp.fullName, position: emp.employeeInformationPositionDescription });
	};

	const handleSelectSupervisor = (emp) => {
		handleInputChange('authorizeSupervisorId', emp.id);
		handleInputChange('supervisorData', { fullName: emp.fullName, position: emp.employeeInformationPositionDescription });
	};

	if (!isOpen) return null;

	const isCancelled = form.status === 'cancelled';
	const isViewOnly = mode === "view";
	const isApproveMode = mode === "approve";

	const lockRequester = isCancelled || isViewOnly || isApproveMode || (selectedData?.status && selectedData.status !== 'pending' && mode !== "edit");
	const lockApprover = isCancelled || isViewOnly || !isApproveMode || selectedData?.status !== 'pending';

	return (
		<div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
			<div className="bg-gray-50 w-full md:w-[95%] lg:w-[85%] rounded-lg shadow-xl h-[90vh] flex flex-col relative">

				{/* Header */}
				<div className="flex-shrink-0 flex justify-between items-center px-6 py-3 border-b bg-white rounded-t-lg shadow-sm">
					<h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
						{isApproveMode ? <RateReviewIcon className="text-orange-500" /> : <PersonSearchIcon className="text-blue-500" />}
						{isApproveMode ? "Process Request" : isViewOnly ? "Request Details" : "Employee Request Form"}
					</h2>
					<button onClick={onClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer"><CloseIcon /></button>
				</div>

				{/* Body */}
				<div className="p-6 overflow-y-auto flex-grow flex flex-col space-y-6">
					<div className="flex-shrink-0 flex flex-wrap gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
						<div className="flex-1"><p className="text-[10px] text-gray-400 uppercase font-bold">Requester</p><p className="text-sm font-medium">{selectedData?.requesterEmployeePersonalInformationFullName || user?.employeePersonalInformationFullName}</p></div>
						<div className="flex-1">
							<p className="text-[10px] text-gray-400 uppercase font-bold">Status</p>
							<span className={`px-2 py-0.5 text-[10px] rounded-full font-bold 
								${form.status === 'approved' ? 'bg-green-100 text-green-700' :
									form.status === 'cancelled' ? 'bg-gray-200 text-gray-700' :
										form.status === 'rejected' ? 'bg-red-100 text-red-700' :
											'bg-yellow-100 text-yellow-700'}`}>
								{form.status?.toUpperCase()}
							</span>
						</div>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:items-stretch flex-grow">
						{/* Left Column */}
						<div className="lg:col-span-5 flex flex-col space-y-4">
							<div className="border border-gray-300 rounded-lg bg-white px-4 py-4 shadow-sm flex-shrink-0">
								<h3 className="font-medium text-gray-700 mb-3 border-b pb-1">Target Employee</h3>
								<EmployeePickerField
									label="Employee:"
									isRequired={true}
									employeePersonalInformationId={form.employeePersonalInformationId}
									displayValue={form.employeeData ? `${form.employeeData.employeeId} - ${form.employeeData.fullName}` : ""}
									onSelect={handleSelectTarget}
									onClear={() => { if (!lockRequester) { handleInputChange('employeePersonalInformationId', ""); handleInputChange('employeeData', null); } }}
									// error={errorState.employee && !form.employeeData} Carloz : fix this soon 
									error={false}
									errorMessage="Please select an employee"
									allowClear={false}
									viewMode={lockRequester}
								/>
								{form.employeeData && (
									<div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-3">
										<div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">{form.employeeData.fullName[0]}</div>
										<div>
											<p className="text-xs font-bold text-blue-900">{form.employeeData.fullName}</p>
											<p className="text-[10px] text-blue-700">{form.employeeData.position} | {form.employeeData.department}</p>
										</div>
									</div>
								)}
							</div>

							<div className="border border-gray-300 rounded-lg bg-white px-4 py-4 shadow-sm space-y-4 flex-grow">
								<h3 className="font-medium text-gray-700 mb-1 border-b pb-1">Routing</h3>
								<EmployeePickerField
									label="Assign Approver:"
									isRequired={true}
									employeePersonalInformationId={form.approverId}
									displayValue={form.approverData ? form.approverData.fullName : ""}
									onSelect={handleSelectApprover}
									onClear={() => {
										if (!lockRequester) {
											handleInputChange('approverId', "");
											handleInputChange('approverData', null);
										}
									}}
									error={false}
									errorMessage="Please select an approver"
									allowClear={false}
									viewMode={lockRequester}
								/>
								<EmployeePickerField
									label="Assign Supervisor:"
									isRequired={true}
									employeePersonalInformationId={form.authorizeSupervisorId}
									displayValue={form.supervisorData ? form.supervisorData.fullName : ""}
									onSelect={handleSelectSupervisor}
									onClear={() => {
										if (!lockRequester) {
											handleInputChange('authorizeSupervisorId', "");
											handleInputChange('supervisorData', null);
										}
									}}
									error={false}
									errorMessage="Please select a supervisor"
									allowClear={false}
									viewMode={lockRequester}
								/>
							</div>
						</div>

						{/* Right Column */}
						<div className="lg:col-span-7 flex flex-col space-y-4">
							<div className="border border-gray-300 rounded-lg bg-white px-4 py-4 shadow-sm flex flex-col flex-grow min-h-0">
								<h3 className="font-medium text-gray-700 mb-3 border-b pb-1 flex-shrink-0">Request Content</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-shrink-0">
									<Input label="Request Number:" type="number" placeholder="Enter Request Number" value={form.requestNumber} onChange={(e) => handleInputChange('requestNumber', e.target.value)} disabled={lockRequester} />
									<DateInput label="Effective Date:" value={form.effectiveDate} onChange={(e) => handleInputChange('effectiveDate', e.target.value)} disabled={lockRequester} />
									<Input label="Request Code:" value={form.requestCode} onChange={(e) => handleInputChange('requestCode', e.target.value)} disabled={lockRequester} placeholder="e.g. PROM, TRANS, SAL" />
									<Select label="Requested Vessel:" value={form.vesselId} options={options.vessels} onChange={(e) => handleInputChange('vesselId', e.target.value)} disabled={lockRequester} />
									<div className="md:col-span-2">
										<Input label="Title/Position Requested:" placeholder="-- No Data --" value={form.title} onChange={(e) => handleInputChange('title', e.target.value)} disabled={true} />
									</div>
								</div>
								<div className="mt-4 flex flex-col flex-grow min-h-0">
									<TextArea label="Requester Remarks:" placeholder="Enter your remarks here" value={form.remarks} onChange={(e) => handleInputChange('remarks', e.target.value)} disabled={lockRequester} rows={1} containerClassName="flex-grow min-h-0" className="flex-grow !resize-none overflow-y-auto" />
								</div>
							</div>

							{(isApproveMode || form.status !== 'pending') && (
								<div className="border border-orange-200 rounded-lg bg-orange-50/30 p-4 shadow-sm flex-shrink-0">
									<h3 className="font-medium text-orange-800 mb-3 border-b border-orange-200 pb-1 flex-shrink-0">Approver Review</h3>
									<TextArea label="Approver Remarks:" placeholder="Enter your remarks here" value={form.approverRemarks} onChange={(e) => handleInputChange('approverRemarks', e.target.value)} disabled={lockApprover} rows={2} />
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 flex-shrink-0">
										<div className="hidden md:block"></div>
										<Input label="Authorized Person:" value={form.authorizedSignatureVP} onChange={(e) => handleInputChange('authorizedSignatureVP', e.target.value)} disabled={lockApprover} />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Footer */}
				<div className="flex-shrink-0 p-4 border-t bg-white flex justify-end space-x-2 rounded-b-lg shadow-inner">
					<button onClick={onClose} className="px-6 py-2 bg-gray-400 text-white rounded border-none cursor-pointer hover:bg-gray-500 transition-colors">Close</button>

					{!isViewOnly && !isApproveMode && mode === "edit" && !isCancelled && (
						<button onClick={handleCancel} className="px-6 py-2 bg-red-500 text-white rounded border-none cursor-pointer hover:bg-red-600 font-bold transition-colors">
							Cancel Request
						</button>
					)}

					{!lockRequester && (
						<button onClick={handleSave} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded border-none cursor-pointer hover:bg-blue-700 font-bold transition-colors">
							{mode === "add" ? "Submit" : "Update"}
						</button>
					)}

					{isApproveMode && selectedData?.status === 'pending' && !isCancelled && (
						<>
							<button onClick={() => handleApproveAction(false)} className="px-6 py-2 bg-red-500 text-white rounded border-none cursor-pointer font-bold hover:bg-red-600 transition-colors">Reject</button>
							<button onClick={() => handleApproveAction(true)} className="px-6 py-2 bg-green-600 text-white rounded border-none cursor-pointer font-bold hover:bg-green-700 transition-colors">Approve</button>
						</>
					)}
				</div>
			</div>

			<ConfirmationModal
				visible={showConfirmation}
				onClose={() => setShowConfirmation(false)}
				onConfirm={confirmationConfig.action}
				title={confirmationConfig.title}
				message={confirmationConfig.message}
				type={confirmationConfig.type}
				confirmText="Confirm"
				cancelText="Cancel"
				isLoading={loading}
			/>

			<LoadingSpinner loading={loading || dropdownsLoading} />
		</div>
	);
};